import { type BulkRequestQuery, BulkRequestQueryKey, useBulkRequestQuery } from "api/clients/useBulkRequestQuery";
import { getRefetchInterval, useRefetchMeta } from "@vyne-shared/utils/invalidateQueriesFor";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

const refetchIntervalWhenBusy = 4000;

/**
 * Client hook to query for Patients/Responsible Parties that are eligible for bulk import.
 * Re-fetches when user returns to the Bulk Request tab and the query is stale or indicating a busy state.
 * @param enabled Indicates that the Bulk Request tab is selected.
 * @returns React Query properties.
 */
export function useQueryWithInvalidate(enabled: boolean) {
    // Retrieve data from the back-end
    // This serves as the baseline data
    const result = useBulkRequestQuery({
        enabled,

        // Keep data around for a short while - this could be a lot of data, so we don't want to constantly
        // re-fetch, but we don't want it to get too out of date either.
        staleTime: 10 * 60 * 1000, // 10 minutes

        // no need to waste time processing large results, and busyQuery is shared anyway in the busy case
        structuralSharing: false,

        // facilitate auto-refetch after payment transaction
        meta: useRefetchMeta(2000),

        // Automatically re-fetch periodically as long as the bulk request is in a busy (processing state), and bulk tab
        // is active. In typical cases (ex. while the user is working on selecting rows), don't auto-refetch. When the
        // system is busy with another request (and showing the BulkRequestInProgress hourglass instead of a table), do
        // auto-refetch, so that if the user is monitoring the bulk request tab, they'll see when the in-progress bulk
        // operation has finished.
        refetchInterval: (qry) => (qry.state.data?.busy ? refetchIntervalWhenBusy : getRefetchInterval(qry)),
    });

    // re-fetch when user returns to the Bulk Request tab and the query is stale or indicating a busy state
    useInvalidate(enabled);

    return result;
}

function useInvalidate(enabled: boolean) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (enabled) {
            void queryClient.invalidateQueries({
                queryKey: BulkRequestQueryKey,
                type: "active",
                fetchStatus: "idle",

                predicate: (qry) => {
                    const data = qry.state.data as BulkRequestQuery | undefined;

                    // upon switch to Bulk Request Tab, go ahead and fetch in these scenarios:
                    // - we're already stale
                    // - a bulk operation is in progress (and we're showing the in-progress hourglass)
                    return qry.isStale() || !!data?.busy;
                },
            });
        }
    }, [enabled, queryClient]);
}
